import React, { useState } from 'react';
// import { CrudAddModalProps, CrudEditModalProps } from '../crud/CrudTable';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import { Button, Container, FileButton, Group, Loader, Stack, TextInput, Tooltip } from '@mantine/core';
import { useForm, schemaResolver } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconFilePlus } from '@tabler/icons-react';
import StringUtils from '@dr-pam/common-components/Utils/StringUtils';
import { v4 as uuid } from 'uuid';
import type { SectionImageListFragment, SectionImageSingleFragment } from '../../graphql/graphql';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import { useS3ObjectStorageService } from '@dr-pam/common-components/Services/S3ObjectStorageService';
import z from 'zod';
import { useSectionImageService } from '../../services/SectionImageService';
import type { AddImageForm } from './AddCategoryImageModal';

export const validationSchema = z.object({
	filename: z.string().min(1).max(512),
});

export type EditCategoryImageModalProps = {
	modalId: string;
	current: SectionImageListFragment;
	categoryId?: string;
	onEdited: (sectionImage: SectionImageSingleFragment) => void;
	onCancel: () => void;
};

export default function EditCategoryImageModal(props: EditCategoryImageModalProps) {
	const { modalId, onCancel, onEdited, current, categoryId } = props;

	const { addLoader, removeLoader, isLoading } = useLoadTracker();
	const [fileToUpload, setFileToUpload] = useState<File | null>(null);

	const s3ObjectStorageService = useS3ObjectStorageService();
	const sectionImageService = useSectionImageService();

	const form = useForm<EditImageForm>({
		initialValues: {
			url: current.url ?? '',
			filename: current?.filename?.replace(/^category-images\/[^/]+\/[^/]+\/[^/]+\//, '') ?? '',
		},
		validate: schemaResolver(validationSchema),
	});

	const handleFileSelected = (file: File | null) => {
		if (file) {
			form.setValues({
				url: StringUtils.slugifyForFilesystem(file.name),
				filename: file.name,
			});
		} else {
			form.setValues({
				url: '',
				filename: '',
			});
		}
		setFileToUpload(file);
	};

	const handleSubmit = async (values: EditImageForm) => {
		const loader = addLoader();

		try {
			let url: string | null = null;

			if (fileToUpload) {
				const imageId = uuid();
				const taggedFileName = `category-images/${categoryId}/${imageId}/${Date.now()}/${fileToUpload.name}`;
				const uploadRequest = s3ObjectStorageService.uploadFile(taggedFileName, fileToUpload);
				await uploadRequest.response;
				url = s3ObjectStorageService.getRedirectUrl(taggedFileName);
			}

			const updatedSectionImage = await sectionImageService.update(current.id ?? '', {
				url: { set: url ?? values.url },
				filename: { set: fileToUpload?.name ?? values.filename },
			});

			onEdited(updatedSectionImage);
			NotificationUtils.showSuccess('Successfully updated category image', updatedSectionImage.filename ?? '');
			modals.close(modalId);
		} catch (err) {
			NotificationUtils.showError(err as Error, 'Failed to update category image');
			removeLoader(loader);
		}
	};

	const handleCancel = () => {
		onCancel?.();
		modals.close(modalId);
	};

	return (
		<Container>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack>
					<Group align="flex-end" justify="flex-end">
						<TextInput
							label="Upload file"
							{...form.getInputProps('filename')}
							withAsterisk
							disabled
							style={{ flexGrow: 1 }}
						/>
						<FileButton onChange={handleFileSelected}>
							{(props) => (
								<Tooltip label="Click to upload file">
									<Button {...props}>
										<IconFilePlus />
									</Button>
								</Tooltip>
							)}
						</FileButton>
					</Group>
					<Group justify="flex-end" mt="md">
						<Button type="button" variant="subtle" onClick={handleCancel} disabled={isLoading}>
							Cancel
						</Button>
						<Button type="submit" disabled={isLoading}>
							Update image {isLoading && <Loader size="xs" ml="xs" />}
						</Button>
					</Group>
				</Stack>
			</form>
		</Container>
	);
}

export type EditImageForm = Omit<AddImageForm, 'categoryId'>;
